import React from 'react';
import { useLang } from '@/i18n/LanguageContext';
import { Container, SectionHeading, SectionKicker, TricolourRibbon } from '@/components/common/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowRight, CheckCircle2, Sparkles, Gauge, ScanSearch, Wrench, ShieldCheck,
  Repeat, TrendingUp, XCircle, HelpCircle,
} from 'lucide-react';
import { FOUNDING_VISION, SELF_CORRECTING } from '@/data/foundingContent';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function VisionPage() {
  const { t } = useLang();
  const V = FOUNDING_VISION;

  return (
    <div>
      <section className="hero-surface">
        <Container className="py-14 sm:py-20">
          <SectionKicker>{t('vision_page.title')}</SectionKicker>
          <h1 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0B1220] max-w-4xl">
            {t('vision_page.lede')}
          </h1>
          <div className="mt-6 space-y-2 max-w-3xl">
            {V.lede.map((line, i) => (
              <p key={i} className="text-lg sm:text-xl text-[#062B5C] font-medium">
                {line}
              </p>
            ))}
          </div>
          <p className="mt-6 max-w-3xl text-[#5B6B82] leading-relaxed">{V.intro}</p>
        </Container>
      </section>

      {/* Why reform */}
      <section className="py-16">
        <Container>
          <SectionHeading title={t('vision_page.why_reform')} />
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <Card className="border-[#D7E1EF] bg-white">
                <CardContent className="p-5">
                  <div className="text-sm font-semibold text-[#062B5C]">India has:</div>
                  <ul className="mt-4 space-y-2">
                    {V.why_reform.strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-[#0B1220]">
                        <CheckCircle2 className="h-4 w-4 text-[#138A4B] mt-0.5" />
                        <span className="text-sm">{s}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
            <div className="lg:col-span-7">
              <p className="text-sm text-[#5B6B82] mb-4">
                Yet ordinary citizens and businesses still experience friction. Ask the honest questions:
              </p>
              <div className="space-y-3">
                {V.why_reform.questions.map((q, i) => (
                  <div key={i} className="rounded-lg border border-[#D7E1EF] bg-white p-4">
                    <div className="text-sm text-[#0B1220] leading-relaxed">“{q}”</div>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-sm text-[#062B5C] font-medium">{V.why_reform.closing}</p>
            </div>
          </div>
        </Container>
      </section>

      {/* From -> To transitions */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading title={t('vision_page.transitions_title')} />
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {V.from_to_transitions.map((tr, i) => (
              <div key={i} className="flex items-center justify-between gap-4 rounded-lg border border-[#D7E1EF] bg-[#F5F8FC] p-4">
                <div className="text-sm text-[#5B6B82] line-through decoration-[#B42318]/50">{tr.from}</div>
                <ArrowRight className="h-4 w-4 text-[#062B5C] shrink-0" />
                <div className="text-sm font-semibold text-[#0B1220]">{tr.to}</div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-[#5B6B82]">Government should become easier to deal with when a citizen follows the law. That is governance reform.</p>
        </Container>
      </section>

      {/* Measurable Performance */}
      <section className="py-16">
        <Container>
          <SectionHeading title={V.measurable_performance.heading} subtitle={V.measurable_performance.intro} />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {V.measurable_performance.domains.map((d, i) => (
              <Card key={i} className="border-[#D7E1EF] bg-white">
                <CardContent className="p-5">
                  <div className="text-sm font-semibold text-[#062B5C]">{d.area}</div>
                  <ul className="mt-3 space-y-1.5">
                    {d.metrics.map((m) => (
                      <li key={m} className="text-sm text-[#0B1220] flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#F47B20]" />
                        {m}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="mt-6 text-sm text-[#5B6B82] max-w-3xl">{V.measurable_performance.closing}</p>
        </Container>
      </section>

      {/* 10 Principles */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading eyebrow="Principles" title={t('vision_page.principles_title')} />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
            {V.principles.map((p) => (
              <Card key={p.number} className="border-[#D7E1EF] bg-white" data-testid="principle-card">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-lg bg-[#062B5C] text-white flex items-center justify-center font-semibold text-sm">
                      {String(p.number).padStart(2, '0')}
                    </div>
                    <div className="font-display font-semibold text-[#0B1220]">{p.title}</div>
                  </div>
                  <p className="mt-3 text-sm text-[#5B6B82] leading-relaxed">{p.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 rounded-xl border border-[#D7E1EF] bg-[#F5F8FC] p-6">
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">Formula</div>
            <div className="mt-2 font-display text-lg sm:text-xl font-semibold text-[#0B1220]">
              {V.formula}
            </div>
          </div>
        </Container>
      </section>

      {/* SELF-CORRECTING GOVERNANCE */}
      <SelfCorrectingSection />

      {/* What NGRP is trying to build */}
      <section className="py-16">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <SectionHeading title={t('vision_page.what_ngrp')} />
            </div>
            <div className="lg:col-span-7">
              <Card className="border-[#D7E1EF] bg-white">
                <CardContent className="p-6">
                  <p className="text-[#0B1220] leading-relaxed">{t('vision_page.what_ngrp_body')}</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </Container>
      </section>

      {/* Message + Promise */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-[#D7E1EF] bg-white">
              <CardContent className="p-6">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[#062B5C]">{t('vision_page.message_title')}</div>
                <p className="mt-3 text-[#0B1220] leading-relaxed">{V.message}</p>
              </CardContent>
            </Card>
            <Card className="border-[#D7E1EF] bg-[#062B5C] text-white">
              <CardContent className="p-6">
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-white/80">{t('vision_page.promise_title')}</div>
                <p className="mt-3 text-white leading-relaxed">{V.promise}</p>
                <div className="mt-5 flex items-center gap-2 text-sm text-white/80">
                  <Sparkles className="h-4 w-4" /> {V.motto_chain}
                </div>
              </CardContent>
            </Card>
          </div>
        </Container>
      </section>

      {/* Our Call */}
      <section className="py-16">
        <Container>
          <SectionHeading title={t('vision_page.call_title')} subtitle="To every Indian who is willing to help build better institutions:" />
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {V.call.items.map((c, i) => (
              <div key={i} className="rounded-lg border border-[#D7E1EF] bg-white p-4">
                <div className="text-xs uppercase tracking-[0.14em] font-semibold text-[#062B5C]">{c.audience}</div>
                <div className="mt-2 text-sm text-[#0B1220] leading-relaxed">{c.body}</div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/join">
              <Button className="bg-[#062B5C] text-white hover:bg-[#05244d]" data-testid="vision-join-cta">{t('common.join_cta')}</Button>
            </Link>
            <Link to="/aspirant">
              <Button variant="outline" className="border-[#D7E1EF] text-[#062B5C]" data-testid="vision-aspirant-cta">{t('common.apply_cta')}</Button>
            </Link>
          </div>
        </Container>
      </section>

      <TricolourRibbon />

      <section className="py-14 bg-[#F5F8FC]">
        <Container>
          <div className="text-center max-w-2xl mx-auto">
            <p className="font-display text-2xl sm:text-3xl font-semibold text-[#0B1220]">{V.purpose_line}</p>
            <div className="mt-4 text-sm text-[#062B5C] font-medium tracking-wide">{V.closing_line}</div>
          </div>
        </Container>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Self-Correcting Governance section
// ---------------------------------------------------------------------------
const CYCLE_ICONS = {
  measure: Gauge,
  detect: ScanSearch,
  correct: Wrench,
  verify: CheckCircle2,
  improve: TrendingUp,
  prevent: ShieldCheck,
};

function SelfCorrectingSection() {
  const S = SELF_CORRECTING;
  return (
    <>
      {/* Intro */}
      <section className="py-16 bg-[#062B5C] text-white relative overflow-hidden">
        <TricolourRibbon className="absolute top-0 left-0 right-0" />
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/80">
                {S.eyebrow}
              </div>
              <h2 className="font-display mt-3 text-3xl sm:text-4xl font-semibold text-white tracking-[-0.01em]">
                {S.title}
              </h2>
              <p className="mt-5 text-white/85 leading-relaxed max-w-2xl">{S.lede}</p>
              <p className="mt-4 text-white/85 leading-relaxed max-w-2xl">{S.intent}</p>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-2xl bg-white/10 border border-white/15 p-6 backdrop-blur-sm">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">
                  {S.principle.label}
                </div>
                <p className="mt-3 font-display text-lg sm:text-xl font-semibold text-white leading-snug">
                  &ldquo;{S.principle.body}&rdquo;
                </p>
                <div className="mt-5 grid grid-cols-1 gap-2">
                  <PrincipleStripe icon={XCircle} text="No protest for routine rights" />
                  <PrincipleStripe icon={XCircle} text="No bribe for public services" />
                  <PrincipleStripe icon={XCircle} text="No political recommendation for legitimate work" />
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* Cycle */}
      <section className="py-16 bg-white border-b border-[#D7E1EF]">
        <Container>
          <SectionHeading
            eyebrow="Continuous governance reform"
            title="Government should operate through a permanent cycle"
            subtitle="Measure → Detect → Correct → Verify → Improve → Prevent Recurrence"
          />
          <div
            className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3"
            data-testid="self-correcting-cycle"
          >
            {S.cycle.map((step, i) => {
              const Icon = CYCLE_ICONS[step.key] || Repeat;
              return (
                <div
                  key={step.key}
                  className="relative rounded-xl border border-[#D7E1EF] bg-white p-5"
                  data-testid="self-correcting-cycle-step"
                >
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#062B5C]">
                    Step {i + 1}
                  </div>
                  <div className="mt-3 h-10 w-10 rounded-lg bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center text-[#062B5C]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="mt-3 font-display font-semibold text-[#0B1220]">{step.title}</div>
                  <p className="mt-2 text-xs text-[#5B6B82] leading-relaxed">{step.body}</p>
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* Department questions */}
      <section className="py-16 bg-[#F5F8FC]">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow="Every department, every quarter"
                title="Eight questions every department should keep asking"
                subtitle="These are the honest, structural questions that separate self-correcting governance from busywork."
              />
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {S.department_questions.map((q, i) => (
                  <div
                    key={i}
                    className="flex items-start gap-3 rounded-lg bg-white border border-[#D7E1EF] p-4"
                  >
                    <span className="h-7 w-7 rounded-md bg-[#F5F8FC] border border-[#D7E1EF] flex items-center justify-center text-[#062B5C] shrink-0">
                      <HelpCircle className="h-3.5 w-3.5" />
                    </span>
                    <div className="text-sm text-[#0B1220] leading-relaxed">{q}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* From complaint-based to self-correcting */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading
            eyebrow="From complaint-based to self-correcting"
            title="Government should discover its problems before citizens are forced to protest about them"
          />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            {S.from_complaint_to_self_correcting.map((row, i) => (
              <div
                key={i}
                className="rounded-xl border border-[#D7E1EF] bg-white p-5"
                data-testid="self-correcting-transition"
              >
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#B54708]">
                  Today
                </div>
                <p className="mt-2 text-sm text-[#5B6B82] leading-relaxed line-through decoration-[#B42318]/40 decoration-1">
                  {row.from}
                </p>
                <div className="mt-4 flex items-center gap-2 text-[#062B5C]">
                  <ArrowRight className="h-4 w-4" />
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em]">NGRP</div>
                </div>
                <p className="mt-2 text-sm text-[#0B1220] font-medium leading-relaxed">{row.to}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Accountability chain */}
      <section className="py-16 bg-[#F5F8FC]">
        <Container>
          <SectionHeading
            eyebrow="Built into the system"
            title="Every public service should carry the same six answers"
            subtitle="Citizens should be able to track a government decision as easily as they track a bank transaction or a delivery."
          />
          <div className="mt-10 flex flex-col lg:flex-row items-stretch gap-2">
            {S.accountability_chain.map((step, i) => (
              <React.Fragment key={step.key}>
                <div
                  className="flex-1 rounded-xl border border-[#D7E1EF] bg-white p-5"
                  data-testid="accountability-chain-step"
                >
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#062B5C]">
                    Step {i + 1}
                  </div>
                  <div className="mt-2 font-display font-semibold text-[#0B1220]">{step.title}</div>
                  <p className="mt-2 text-xs text-[#5B6B82] leading-relaxed">{step.body}</p>
                </div>
                {i < S.accountability_chain.length - 1 && (
                  <div className="hidden lg:flex items-center text-[#062B5C]/50">
                    <ArrowRight className="h-4 w-4" />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </Container>
      </section>

      {/* Goals */}
      <section className="py-16 bg-white border-y border-[#D7E1EF]">
        <Container>
          <SectionHeading eyebrow="The ultimate goal" title="Measure success by problems prevented, not just problems solved" />
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
            {S.goals.map((g, i) => (
              <div
                key={i}
                className="rounded-xl border border-[#D7E1EF] bg-[#F5F8FC] p-5 flex items-start gap-3"
              >
                <span className="h-8 w-8 rounded-lg bg-white border border-[#D7E1EF] flex items-center justify-center text-[#138A4B] shrink-0">
                  <Sparkles className="h-4 w-4" />
                </span>
                <p className="text-sm text-[#0B1220] leading-relaxed">{g}</p>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* Vision headline */}
      <section className="py-16 bg-[#062B5C] text-white">
        <Container>
          <div className="max-w-4xl">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-white/80">
              NGRP Vision
            </div>
            <h3 className="font-display mt-3 text-3xl sm:text-4xl font-semibold text-white tracking-[-0.01em]">
              {S.headline}
            </h3>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {S.vision_promises.map((v, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 rounded-lg bg-white/10 border border-white/15 px-4 py-3"
                  data-testid="vision-promise"
                >
                  <XCircle className="h-4 w-4 text-white/80 shrink-0" />
                  <span className="text-sm text-white font-medium">{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-10 rounded-2xl bg-white text-[#062B5C] p-6 sm:p-8">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#062B5C]/80">
                The tagline
              </div>
              <p className="mt-2 font-display text-2xl sm:text-3xl font-semibold leading-snug">
                {S.tagline}
              </p>
              <p className="mt-3 text-sm text-[#0B1220]">{S.standard}</p>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}

function PrincipleStripe({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-white/5 border border-white/15 px-3 py-2">
      <Icon className="h-3.5 w-3.5 text-white/70 shrink-0" />
      <div className="text-xs sm:text-sm text-white">{text}</div>
    </div>
  );
}
